import { TestButtonStatus } from '@labrav/react-components/lib/@types/components/TestButtonComponent'
import { Reducer } from 'redux'
import { AccountsActions } from './accountsActions'
import { InputsFields } from '@labrav/react-components/lib/@types/components/CFTemplateView'
import { isEmpty } from 'lodash'
import {
  IamArnRegex,
  awsCasS3BucketNameRegex,
  snsTopicARNPattern,
} from '../../../common/utils/constants'
import { FlyOutListingUserType } from '../../../admin/modules/flyOutSyncs/types'

export enum TypeOfAccount {
  ORGANIZATION_ROOT = 'ORGANIZATION_ROOT',
  ORGANIZATION_UNIT = 'ORGANIZATION_UNIT',
  ORGANIZATION_MEMBER = 'ORGANIZATION_MEMBER',
}
export interface TemplateRows {
  id: string
  name?: string
  version?: string
  hash: string
  productId: string
  isDefault?: boolean
  template: string
  description: string
  title?: string
  iamRoleArn?: string
  awsTemplateId?: string
  testStatus?: TestButtonStatus
  inputsFields: InputsFields[]
}

export interface AzureBackendAccountDetails {
  id?: string
  crm_id: string
  tenant_id?: string
  mpn_id: string
  company_name: string
  contact_email: string
  is_internal_account: boolean
  industry: string
  partner_type: string[]
  is_isv: boolean
  has_access_to_mpc_account_as_admin_user: boolean
  separate_user_for_labra_integration_created_in_mpc: boolean
  reseller: boolean
  solution_must_be_configured_with_mpc: boolean
  microsoft_account_name: string
  azure_config_metadata: AzureConfigMetaDataResponse
  azure_solution_data: AzureSolution[]
  status?: string
}
export type AzureConfigMetaDataResponse = {
  steps: {
    prerequisites: boolean
    mpc_account_details: boolean
    microsoft_product_details: boolean
    link_mpc_account: boolean
  }
  is_azure_onboarding_completed: boolean
}
export interface AzureSolution {
  id?: string
  solution_name: string
  solution_id: string
  publisher_name: string
  solution_type: string
}

export interface AzureAccountDetails {
  id?: string
  fetchedForPartner?: string
  name: string
  authUri?: string
  state?: string
  scopes?: string[]
  MPNID: string
  ISV: boolean
  grantConsent: boolean
  terms: boolean
  microsoftPartnerType: string[]
  industries: string
  status?: string
  solutionMustBeConfiguredWithMpc?: boolean
  separateUserForLabraIntegrationCreatedInMpc?: boolean
  microsoftAccountName?: string
  reseller?: boolean
  hasAccessToMpcAccountAsAdminUser?: boolean
  azureConfigMetadata?: AzureConfigMetaDataResponse
  azureSolutionsData?: AzureSolution[]
}

export interface AWSAccountDetails {
  externalAccountId?: string
  type?: string
  name?: string
  roles: {
    rows: TemplateRows[]
  }
  origin?: FlyOutListingUserType
  partnershipTier?: string
  partnershipTypes?: string
  industries?: string
  terms?: boolean
  isIsv?: boolean
  isSrrp?: boolean
  accelerateProgram?: boolean
}

export interface AccountsState {
  cloudType: string
  productId?: string
  organizationId?: string

  accountDetails: {
    AWS: AWSAccountDetails
    AZURE: AzureAccountDetails
    GCP: Record<string, unknown>
    REDHAT: Record<string, unknown>
  }
  // Separate selected account details for form display (doesn't affect onboarding validation)
  selectedAccountDetails: {
    AWS: AWSAccountDetails
    AZURE: AzureAccountDetails
    GCP: Record<string, unknown>
    REDHAT: Record<string, unknown>
  }
  selectedAccountId?: string // Track which account is currently selected
}
export interface GetUpdatedInputFieldErrorArgs {
  keyName: string
  value: string
}

export const getUpdatedInputFieldError = ({
  keyName,
  value,
}: GetUpdatedInputFieldErrorArgs) => {
  switch (keyName) {
    case 'awsRoleArn':
    case 'iamRoleArn': {
      if (isEmpty(value)) return 'ARN is required'
      else if (!IamArnRegex.test(value)) {
        return 'Please enter valid ARN'
      } else {
        return ''
      }
    }
    case 'awsCasS3BucketName': {
      if (isEmpty(value)) return 'S3 CAS Bucket Name is required'
      else if (!awsCasS3BucketNameRegex.test(value)) {
        return 'Please enter valid S3 CAS Bucket Name'
      } else {
        return ''
      }
    }
    case 'awsCasSnsTopicArn': {
      if (isEmpty(value)) return 'CAS SNS Topic ARN is required'
      else if (!snsTopicARNPattern.test(value)) {
        return 'Please enter valid CAS SNS Topic ARN'
      } else {
        return ''
      }
    }
    default: {
      return ''
    }
  }
}

export const initialState: AccountsState = {
  cloudType: '',
  productId: '',
  organizationId: '',
  accountDetails: {
    AWS: {
      externalAccountId: '',
      type: '',
      name: '',
      roles: {
        rows: [],
      },
    },
    AZURE: {
      name: '',
      ISV: false,
      grantConsent: false,
      MPNID: '',
      terms: false,
      microsoftPartnerType: [],
      industries: '',
    },
    GCP: {},
    REDHAT: {},
  },
  selectedAccountDetails: {
    AWS: {
      externalAccountId: '',
      type: '',
      name: '',
      roles: {
        rows: [],
      },
    },
    AZURE: {
      name: '',
      ISV: false,
      grantConsent: false,
      MPNID: '',
      terms: false,
      microsoftPartnerType: [],
      industries: '',
    },
    GCP: {},
    REDHAT: {},
  },
  selectedAccountId: undefined,
}

type AccountOnBoardingRolesAndPartnershipType = {
  roles: TemplateRows[]
  partnershipTypes?: string
}

export interface SetAccountOnboardingDataPayload {
  cloudType: string
  organizationId: string
  productId: string
  externalId: string
  type: string
  name: string
  industries?: string
  partnershipTier?: string
  terms?: boolean
  isIsv: boolean
  isSrrp?: boolean
  accelerateProgram?: boolean
  origin?: FlyOutListingUserType
}

type actionType =
  | {
      type: AccountsActions.SET_ACCOUNT_ONBOARDING_DATA
      payload: SetAccountOnboardingDataPayload &
        AccountOnBoardingRolesAndPartnershipType
    }
  | {
      type: AccountsActions.SET_ACCOUNT_NAME
      payload: string
    }
  | {
      type: AccountsActions.SET_EXTERNAL_ACCOUNT_ID
      payload: string
    }
  | {
      type: AccountsActions.SET_TEMPLATE_INPUT_FIELD
      payload: {
        templateId: string
        keyName: string
        value: string
      }
    }
  | {
      type: AccountsActions.SET_TEST_STATUS_ARN
      payload: {
        templateId: string
        status: TestButtonStatus
        keyName: string
        error?: string
      }
    }
  | {
      type: AccountsActions.UPDATE_VALUE_ON_ACCOUNT_DETAILS_AZURE
      payload: {
        key: string
        value: unknown
      }
    }
  | {
      type: AccountsActions.SET_AZURE_ACCOUNT_DETAILS
      payload: AzureAccountDetails & {
        cloudType?: string
        productId?: string
        organizationId?: string
      }
    }
  | {
      type: AccountsActions.UPDATE_VALUE_ON_ACCOUNT_DETAILS_AWS
      payload: {
        key: string
        value: unknown
      }
    }
  | {
      type: AccountsActions.SET_AZURE_ONBOARDING_COMPLETED
      payload: {
        value: boolean
      }
    }
  | {
      type: AccountsActions.SET_SELECTED_ACCOUNT_DATA
      payload: {
        accountId: string
        data: SetAccountOnboardingDataPayload &
          AccountOnBoardingRolesAndPartnershipType
      }
    }
  | {
      type: AccountsActions.SET_SELECTED_AZURE_ACCOUNT_DETAILS
      payload: {
        accountId: string
        accountDetails: AzureAccountDetails & {
          cloudType?: string
          productId?: string
          organizationId?: string
        }
      }
    }
  | {
      type: AccountsActions.CLEAR_SELECTED_ACCOUNT_DATA
      payload?: {
        cloudType?: string
      }
    }

interface FindIndexOfInputFieldsArgs {
  inputFields: InputsFields[]
  keyName: string
}

export const findIndexOfInputFields = ({
  inputFields,
  keyName,
}: FindIndexOfInputFieldsArgs) => {
  return inputFields.findIndex(inputField => inputField.keyName === keyName)
}

export const reducer: Reducer<AccountsState, actionType> = (
  state = initialState,
  action
) => {
  switch (action.type) {
    case AccountsActions.SET_ACCOUNT_ONBOARDING_DATA: {
      const {
        cloudType,
        organizationId,
        productId,
        externalId,
        name,
        roles,
        partnershipTypes,
        partnershipTier,
        industries,
        terms,
        isIsv,
        isSrrp,
        accelerateProgram,
        origin,
      } = action.payload
      return {
        ...state,
        cloudType,
        organizationId,
        productId,
        accountDetails: {
          ...state.accountDetails,
          AWS: {
            ...state.accountDetails.AWS,
            externalAccountId: externalId || '',
            type: '',
            name: name || '',
            roles: { rows: roles },
            partnershipTier,
            partnershipTypes,
            industries,
            terms,
            isIsv,
            isSrrp,
            accelerateProgram,
            origin,
          },
        },
      }
    }
    case AccountsActions.SET_ACCOUNT_NAME: {
      return {
        ...state,
        accountDetails: {
          ...state.accountDetails,
          AWS: {
            ...state.accountDetails.AWS,
            name: action.payload,
          },
        },
      }
    }
    case AccountsActions.SET_EXTERNAL_ACCOUNT_ID: {
      return {
        ...state,
        accountDetails: {
          ...state.accountDetails,
          AWS: {
            ...state.accountDetails.AWS,
            externalAccountId: action.payload,
          },
        },
      }
    }

    case AccountsActions.SET_TEMPLATE_INPUT_FIELD: {
      const { templateId, keyName, value } = action.payload
      const newRows = state.accountDetails.AWS.roles.rows.map(row => {
        if (row.id === templateId) {
          const updateInputsFields = row.inputsFields
          const inputFieldIndex = findIndexOfInputFields({
            inputFields: row.inputsFields,
            keyName,
          })
          updateInputsFields[inputFieldIndex].value = value
          updateInputsFields[inputFieldIndex].error = getUpdatedInputFieldError(
            { keyName, value }
          )
          updateInputsFields[inputFieldIndex].testStatus =
            'standBy' as TestButtonStatus
          return {
            ...row,
            inputsFields: updateInputsFields,
          }
        }
        return row
      })
      return {
        ...state,
        accountDetails: {
          ...state.accountDetails,
          AWS: {
            ...state.accountDetails.AWS,
            roles: {
              rows: newRows,
            },
          },
        },
      }
    }
    case AccountsActions.SET_TEST_STATUS_ARN: {
      const { templateId, status, keyName, error } = action.payload
      const newRows = state.accountDetails.AWS.roles.rows.map(row => {
        if (row.id === templateId) {
          const updateInputsFields = row.inputsFields
          const inputFieldIndex = findIndexOfInputFields({
            inputFields: row.inputsFields,
            keyName,
          })
          updateInputsFields[inputFieldIndex].testStatus = status
          updateInputsFields[inputFieldIndex].error = error || ''
          return {
            ...row,
            inputsFields: updateInputsFields,
          }
        }
        return row
      })
      return {
        ...state,
        accountDetails: {
          ...state.accountDetails,
          AWS: {
            ...state.accountDetails.AWS,
            roles: {
              rows: newRows,
            },
          },
        },
      }
    }
    case AccountsActions.UPDATE_VALUE_ON_ACCOUNT_DETAILS_AZURE: {
      const { key, value } = action.payload
      return {
        ...state,
        accountDetails: {
          ...state.accountDetails,
          AZURE: {
            ...state.accountDetails.AZURE,
            [key]: value,
          },
        },
      }
    }
    case AccountsActions.UPDATE_VALUE_ON_ACCOUNT_DETAILS_AWS: {
      const { key, value } = action.payload
      return {
        ...state,
        accountDetails: {
          ...state.accountDetails,
          AWS: {
            ...state.accountDetails.AWS,
            [key]: value,
          },
        },
      }
    }
    case AccountsActions.SET_AZURE_ACCOUNT_DETAILS: {
      const accountDetails = action.payload
      return {
        ...state,
        cloudType: accountDetails.cloudType || '',
        productId: accountDetails.productId || '',
        organizationId: accountDetails.organizationId || '',
        accountDetails: {
          ...state.accountDetails,
          AZURE: {
            ...state.accountDetails.AZURE,
            ...accountDetails,
            azureSolutionsData: accountDetails.azureSolutionsData ?? [],
          },
        },
      }
    }
    case AccountsActions.SET_AZURE_ONBOARDING_COMPLETED: {
      const { value } = action.payload
      const defaultMetadata = {
        steps: {
          prerequisites: false,
          mpc_account_details: false,
          microsoft_product_details: false,
          link_mpc_account: false,
        },
        is_azure_onboarding_completed: false,
      }
      const prevPrimaryMetadata =
        state.accountDetails.AZURE.azureConfigMetadata || defaultMetadata
      const prevSelectedMetadata =
        state.selectedAccountDetails.AZURE.azureConfigMetadata ||
        defaultMetadata

      // Settings pages read selectedAccountDetails; onboarding reads accountDetails.
      // Update both when a selected account is active so Save / UI stay in sync.
      const nextSelectedAccountDetails = state.selectedAccountId
        ? {
            ...state.selectedAccountDetails,
            AZURE: {
              ...state.selectedAccountDetails.AZURE,
              azureConfigMetadata: {
                steps: prevSelectedMetadata.steps,
                is_azure_onboarding_completed: value,
              },
            },
          }
        : state.selectedAccountDetails

      return {
        ...state,
        accountDetails: {
          ...state.accountDetails,
          AZURE: {
            ...state.accountDetails.AZURE,
            azureConfigMetadata: {
              steps: prevPrimaryMetadata.steps,
              is_azure_onboarding_completed: value,
            },
          },
        },
        selectedAccountDetails: nextSelectedAccountDetails,
      }
    }
    case AccountsActions.SET_SELECTED_ACCOUNT_DATA: {
      const {
        accountId,
        data: {
          cloudType,
          organizationId,
          productId,
          externalId,
          name,
          roles,
          partnershipTypes,
          partnershipTier,
          industries,
          terms,
          isIsv,
          isSrrp,
          accelerateProgram,
          origin,
        },
      } = action.payload
      return {
        ...state,
        selectedAccountId: accountId,
        selectedAccountDetails: {
          ...state.selectedAccountDetails,
          AWS: {
            ...state.selectedAccountDetails.AWS,
            externalAccountId: externalId || '',
            type: '',
            name: name || '',
            roles: { rows: roles },
            partnershipTier,
            partnershipTypes,
            industries,
            terms,
            isIsv,
            isSrrp,
            accelerateProgram,
            origin,
          },
        },
      }
    }
    case AccountsActions.SET_SELECTED_AZURE_ACCOUNT_DETAILS: {
      const { accountId, accountDetails } = action.payload
      return {
        ...state,
        selectedAccountId: accountId,
        selectedAccountDetails: {
          ...state.selectedAccountDetails,
          AZURE: {
            ...state.selectedAccountDetails.AZURE,
            ...accountDetails,
            azureSolutionsData: accountDetails.azureSolutionsData ?? [],
          },
        },
      }
    }
    case AccountsActions.CLEAR_SELECTED_ACCOUNT_DATA: {
      const cloudType = action.payload?.cloudType
      return {
        ...state,
        selectedAccountId: undefined,
        selectedAccountDetails: {
          ...state.selectedAccountDetails,
          ...(cloudType === 'AWS'
            ? {
                AWS: {
                  externalAccountId: '',
                  type: '',
                  name: '',
                  roles: {
                    rows: [],
                  },
                },
              }
            : cloudType === 'AZURE'
              ? {
                  AZURE: {
                    name: '',
                    ISV: false,
                    grantConsent: false,
                    MPNID: '',
                    terms: false,
                    microsoftPartnerType: [],
                    industries: '',
                  },
                }
              : {}),
        },
      }
    }
    default: {
      return state
    }
  }
}
